'use client';

import { useEffect } from 'react';
import { useAdBlock } from '@/contexts/AdBlockContext';
import { useI18n } from '@/lib/i18n/context';

interface ViewTrackerProps {
  postId: string;
}

const COOKIE_KEY = 'post_viewed';
const MAX_QUEUE = 3;

function getViewedQueue(): string[] {
  if (typeof document === 'undefined') return [];
  
  const raw = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${COOKIE_KEY}=`))
    ?.split('=')[1];

  // console.log('[ViewTracker] raw cookie:', raw);
  
  if (!raw) return [];
  try {
    return JSON.parse(decodeURIComponent(raw));
  } catch (e) {
    console.error('[ViewTracker] parse error:', e);
    return [];
  }
}

function setViewedQueue(queue: string[]) {
  const encoded = encodeURIComponent(JSON.stringify(queue));
  const cookieStr = `${COOKIE_KEY}=${encoded}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`;
  document.cookie = cookieStr;
  // console.log('[ViewTracker] cookie set:', cookieStr);
  // console.log('[ViewTracker] verify:', document.cookie);
}

function pushToQueue(postId: string) {
  const queue = getViewedQueue().filter(id => id !== postId);
  queue.unshift(postId);
  if (queue.length > MAX_QUEUE) queue.length = MAX_QUEUE;
  setViewedQueue(queue);
  // console.log('[ViewTracker] queue after push:', queue);
}

function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

function getCookie(name: string): string {
  const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : '';
}

async function getPushSubscriptionInfo(): Promise<{
  pushEndpoint: string;
  pushP256dh: string;
  pushAuth: string;
}> {
  const empty = { pushEndpoint: '', pushP256dh: '', pushAuth: '' };
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return empty;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return empty;
    const p256dh = sub.getKey('p256dh');
    const auth   = sub.getKey('auth');
    return {
      pushEndpoint: sub.endpoint,
      pushP256dh: p256dh ? btoa(String.fromCharCode(...new Uint8Array(p256dh))) : '',
      pushAuth:   auth   ? btoa(String.fromCharCode(...new Uint8Array(auth)))   : '',
    };
  } catch {
    return empty;
  }
}

function getScreenInfo(locale: string) {
  return {
    deviceType: getDeviceType() + '-' + locale,
    // myxreqId thay thế screen resolution → lưu vào cột screens
    myxreqId: getCookie('MYXREQ_ID'),
    devicePixelRatio: window.devicePixelRatio ?? 1,
  };
}

function getInstallInfo() {
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const isIOSStandalone = (navigator as any).standalone === true;
  return {
    isPWA: isStandalone || isIOSStandalone ? 1 : 0,
  };
}

export function ViewTracker({ postId }: ViewTrackerProps) {
  const { hasAdBlock, checked } = useAdBlock();
  const { locale } = useI18n();

  // Ghi cookie ngay khi checked=true, độc lập với hasAdBlock
  useEffect(() => {
    // if (!checked) return;

    pushToQueue(postId);
  }, [postId, checked]);

  // Gọi API tăng view sau 2s
  useEffect(() => {
    // if (!checked) return;

    const timer = setTimeout(async () => {
      const pushInfo = await getPushSubscriptionInfo();
      fetch(`/api/posts/${postId}/view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          adBlocked: hasAdBlock,
          ...getScreenInfo(locale),
          ...getInstallInfo(),
          // pushEndpoint thay viewport → lưu vào cột viewport
          pushEndpoint: pushInfo.pushEndpoint,
          pushP256dh:   pushInfo.pushP256dh,
          pushAuth:     pushInfo.pushAuth,
        }),
      }).catch(() => {});
    }, 2000);

    return () => clearTimeout(timer);
  }, [postId, checked, hasAdBlock]);

  return null;
}